import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Typography,
  CircularProgress,
  Button,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';

import { useSelector, useDispatch } from 'react-redux';

import PrimaryButton from '../ui/buttons/primaryButton';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import Select from '../ui/forms/fields/select';
import Checkbox from '../ui/forms/fields/checkbox';
import gameFormatService from '../../features/gameFormat/gameFormatService';
import EventMultipleVs from './eventMultipleVs';
import GreenSelector from '../ui/clubControls/greenSelector';
import { createGame } from '../../features/game/gameSlice';
import OverrideGameFormat from '../gameFormats/override';

const schema = yup.object().shape({
  teamA: yup.string().required('Team A is required'),
  teamB: yup.string().required('Team B is required'),
  teamAColor: yup.string(),
  teamBColor: yup.string(),
  teams: yup.object().required('Teams are required'),
  round: yup.number(),
  rink: yup.number(),
  green: yup.string().required('Please select a option.'),
  venue: yup.string().required('Please select a location.'),
  live: yup.boolean(),
  featured: yup.boolean()
});

const AddGameDialogAlt = ({
  section,
  teamAId,
  teamBId,
  customName,
  bracketId,
  matchId,
  matchLocation,
  event,
  open,
  onClose
}) => {
  const dispatch = useDispatch();
  const addGameLoading = useSelector((state) => state.event.addGameLoading);
  const creatingGame = useSelector((state) => state.game.creatingGame);
  // A bracket node can carry the stage's own format (a longer semifinal or
  // final, say), so start from it when there is one.
  const [gameFormat, setGameFormat] = useState(
    matchLocation?.gameFormat || event?.gameFormat
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // A game created from a bracket node starts at the location the draw sheet
  // gave that match. Only prefill when the venue resolved to one of the
  // event's locations; a raw sheet name cannot drive the green list.
  const locationPrefill = matchLocation?.venue ? matchLocation : null;

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      teamAColor: 'green',
      teamBColor: 'red',
      venue: locationPrefill?.venue || event?.venue?.id,
      green: locationPrefill?.green || undefined,
      rink: locationPrefill?.rink ?? undefined
    }
  });

  const errors = methods.formState.errors;

  const createGameName = (data) => {
    let gameFormat = event.gameFormat;
    let gameName = '';

    if (event.venue && data.green !== 'na') {
      gameName = gameName + `${data.green}${data.rink}`;
    }

    if (gameFormat.sections > 0 && section) {
      gameName = gameName + ` - ${section.name}`;
    }

    if (gameFormat.rounds > 0) {
      gameName = gameName + ` - Round ${data.round}`;
    }

    return gameName;
  };

  const onSubmit = async (data) => {
    let gameName = createGameName(data);
    data.name = gameName;

    if (customName) {
      data.name = customName;
    }

    if (event) {
      data.event = event.id;
      data.gameFormat = gameFormat;
      data.gameType = gameFormat.gameType;

      if (!data.venue) {
        data.venue = event.venue.id;
      }
    }

    if (section) {
      data.section = section.id;
    }

    if (bracketId && matchId) {
      data.bracket = bracketId;
      data.match = matchId;
    }

    dispatch(createGame(data))
      .unwrap()
      .then(() => {
        onClose();
        methods.reset();
      })
      .catch((err) => {
        //TODO Error
        console.log(err);
      });
  };

  const onAdd = async () => {
    methods.handleSubmit(onSubmit)();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={'md'}
      fullScreen={isMobile}
      container={document.body}>
      <DialogTitle>Add {customName || 'Game'}</DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <EventMultipleVs selectedTeamA={teamAId} selectedTeamB={teamBId} section={section} />

          <GreenSelector event={event} defaults={locationPrefill} />
          {event.gameFormat.rounds > 0 && (
            <Select name="round" control={methods.control} label={'Round'}>
              {(() => {
                let container = [];
                for (let i = 0; i < event.gameFormat.rounds; i++) {
                  container.push(
                    <MenuItem key={i} value={i + 1}>
                      Round {i + 1}
                    </MenuItem>
                  );
                }
                return container;
              })()}
            </Select>
          )}
          <Stack>
            <Checkbox
              name={'featured'}
              label="Featured"
              control={methods.control}
              error={errors.featured}
              value={event?.featured || false}
            />
            <Checkbox
              name={'live'}
              label="Live Streamed"
              control={methods.control}
              error={errors.live}
              value={event?.live || false}
            />
          </Stack>
          <Typography variant="h6" my={2}>
            Game Information
          </Typography>
          <Typography>Game Format: {gameFormat?.name || event.gameFormat?.name}</Typography>
          <Typography>
            Game Type: {gameFormatService.getGameTypeString(gameFormat?.gameType)}
          </Typography>
        </FormProvider>
        <OverrideGameFormat gameFormat={gameFormat} onChange={(gf) => setGameFormat(gf)} />

        {/* Added Errors yup */}
        <Typography color="error">{errors.venue && errors.venue.message}</Typography>
      </DialogContent>
      <DialogActions>
        {creatingGame ? (
          <CircularProgress />
        ) : (
          <>
            <Button onClick={onClose} onTouchStart={onClose}>
              Cancel
            </Button>
            <PrimaryButton
              onClick={() => onAdd()}
              onTouchStart={() => onAdd()}
              variant={'contained'}
              disabled={creatingGame}>
              Add
            </PrimaryButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddGameDialogAlt;
